import React, { useState } from 'react';
import { formatDate, getFullName, hasPermission } from 'utils';
import Card from 'components/Card/Card';
import { useAsync } from 'react-async-hook';
import { familyService } from 'services';
import { Link, useParams } from 'react-router-dom/cjs/react-router-dom';
import { Spin, Typography, Button, Popconfirm, Tooltip, Dropdown, Space, message, Row, Col } from 'antd';
import Table from 'components/Table';
import { ENDASH, ROLES } from 'constants';
import EmptyState from 'components/EmptyState/EmptyState';
import PageMeta from 'components/PageMeta/PageMeta';
import { Chemistry, ChemistryReference, ChevronDown } from '@carbon/icons-react';
import { useSelector } from 'react-redux';
import { selectRoles } from 'features/auth';

export default function FamilyApplications() {
  const { adminId, householdId } = useParams();
  const [popConfirmIsOpen, setPopConfirmIsOpen] = useState(false);
  const userRoles = useSelector(selectRoles);

  // Get application
  const { loading: applicationLoading, result: application = null } = useAsync(async () => {
    if (adminId) {
      const _applications = await familyService.getApplicationsByUserId(adminId);
      return _applications?.[0] || {};
    }

    if (householdId) {
      return familyService.getHouseHoldApplications(householdId);
    }

    return null;
  }, [adminId, householdId]);

  // Get household details
  const { result: household = null, execute: refetchHousehold } = useAsync(async () => {
    if (householdId) {
      return familyService.getHouseHold(householdId);
    }

    if (adminId && application?.householdId) {
      return familyService.getHouseHold(application?.householdId);
    }

    return null;
  }, [application, adminId, householdId]);

  const _isTestAccount = household?.testRecord === true;
  const _userHasPermissionToToggleAsTest = hasPermission(userRoles, [ROLES.FAMILY_MANAGER]);

  const handleToggleTestAccount = () => {
    if (!_userHasPermissionToToggleAsTest) {
      return;
    }

    const _newTestRecord = !_isTestAccount;
    const currentHouseholdId = householdId || application?.householdId;

    familyService
      .updateHousehold(currentHouseholdId, {
        testRecord: _newTestRecord,
      })
      .then(() => {
        message.success('Done');
        refetchHousehold();
      })
      .catch((error) => {
        console.error('Error updating household', error);
        message.error('Something went wrong');
      });
  };

  if (application === null) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <>
      <PageMeta title="Family user details" />

      <div className="max-w-[1024px] mx-auto mb-8 py-8 px-4 md:py-0 md:px-0">
        {household && (
          <Card
            title="Family user details"
            extra={
              <Popconfirm
                placement="bottomRight"
                description={
                  <div className="w-[70vw] max-w-[250px]">
                    {!_isTestAccount ? (
                      <>
                        You are about to change this to a test account. This will{' '}
                        <strong className="font-semibold">remove</strong> it from processing and reporting.
                        <p className="block mt-4">Are you sure you'd like to proceed?</p>
                      </>
                    ) : (
                      <>
                        You are about to convert this record to a real account. This will{' '}
                        <strong className="font-semibold">include</strong> it in processing and reporting.
                        <p className="block mt-4">Are you sure you'd like to proceed?</p>
                      </>
                    )}
                  </div>
                }
                onConfirm={handleToggleTestAccount}
                onCancel={null}
                okText={!_isTestAccount ? 'Mark as Test' : 'Mark as Real Account'}
                okButtonProps={{ danger: true }}
                cancelText="Cancel"
                open={_isTestAccount ? popConfirmIsOpen : undefined}
                onOpenChange={(open) => {
                  if (_isTestAccount && open === false) {
                    setPopConfirmIsOpen(open);
                  }
                }}
              >
                <Tooltip
                  title={!_userHasPermissionToToggleAsTest ? 'Only Family Managers can toggle the test status' : ''}
                >
                  {!_isTestAccount ? (
                    <Button disabled={!_userHasPermissionToToggleAsTest} icon={<ChemistryReference />}>
                      Mark as Test Account
                    </Button>
                  ) : _userHasPermissionToToggleAsTest ? (
                    <Dropdown
                      menu={{
                        items: [
                          {
                            label: 'Mark as real account',
                            key: 'mark-as-real',
                            onClick: () => {
                              setPopConfirmIsOpen(true);
                            },
                          },
                        ],
                      }}
                      trigger={['click']}
                    >
                      <Button icon={<Chemistry className="mb-[-1px] ml-[-1px] mr-[-2px]" />}>
                        <Space size={3}>
                          Test Account
                          {_userHasPermissionToToggleAsTest && <ChevronDown className="mb-[-1px]" />}
                        </Space>
                      </Button>
                    </Dropdown>
                  ) : (
                    <Button disabled>Test Account</Button>
                  )}
                </Tooltip>
              </Popconfirm>
            }
            className="mb-12"
          >
            <Row gutter={[24, 8]}>
              <Col span={8} className="flex flex-col gap-2">
                <Typography.Text>
                  <span className="!font-semibold">ID:</span> {application?.createdBy?.id ?? ENDASH}
                </Typography.Text>

                <Typography.Text>
                  <span className="!font-semibold">Name:</span>{' '}
                  {application?.applicant ? getFullName(application?.applicant) : household?.name}
                </Typography.Text>

                <Typography.Text>
                  <span className="!font-semibold">Username:</span> {application?.createdBy?.email ?? ENDASH}
                </Typography.Text>
              </Col>

              <Col span={8} className="flex flex-col gap-2">
                <Typography.Text>
                  <span className="!font-semibold">Created date:</span>{' '}
                  {application?.createdDate
                    ? formatDate(application?.createdDate)
                    : household?.createdDate
                    ? formatDate(household?.createdDate)
                    : ENDASH}
                </Typography.Text>

                <Typography.Text>
                  <span className="!font-semibold">Family ID:</span> {application?.householdId || householdId}
                </Typography.Text>

                <Typography.Text>
                  <span className="!font-semibold">Legacy ID:</span> {application?.householdLegacyId ?? ENDASH}
                </Typography.Text>
              </Col>
            </Row>
          </Card>
        )}

        {typeof application?.id === 'number' ? (
          <Card noBodyPadding title="1 Application">
            <Table
              dataSource={application ? [application] : []}
              columns={[
                {
                  title: 'App ID',
                  dataIndex: 'id',
                  render: (id, record) => (
                    <Link
                      onClick={(e) => e.stopPropagation()}
                      className="underline px-3 py-1"
                      to={`/families/${record.householdId}/applications/${id}`}
                    >
                      {id}
                    </Link>
                  ),
                },
                {
                  title: 'Sponsor',
                  dataIndex: 'applicant',
                  render: (text, record) => {
                    return getFullName(record.applicant);
                  },
                },
                {
                  title: 'Start Date',
                  dataIndex: 'startDate',
                  render: (date) => {
                    return date ? formatDate(date) : ENDASH;
                  },
                },
                {
                  title: 'End Date',
                  dataIndex: 'endDate',
                  render: (date) => {
                    return date ? formatDate(date) : ENDASH;
                  },
                },
                {
                  title: 'Status',
                  dataIndex: 'status',
                  render: (_, record) => {
                    const status = record?.status?.status?.description;
                    return status ? <span title={status}>{status}</span> : 'N/A';
                  },
                },
              ]}
              childrenColumnName="_"
              loading={applicationLoading}
            />
          </Card>
        ) : (
          <Card>
            <EmptyState description="No applications found for this family" />
          </Card>
        )}
      </div>
    </>
  );
}
